/*
 * PDP Door V2 — Project 08 "Relaunch Doors" toggle (§3 crux).
 *
 * The door toggle swaps the ADD-TO-CART PRODUCT between a door's subscription
 * parent and its one-time parent — NOT just the selling_plan on one product
 * (that's the old pdp-subscription-disclosure model, deliberately not reused).
 *
 *   Subscribe (default):  form [name=id] = sub variant  · [name=selling_plan] = sub plan
 *   Once ("try once"):     form [name=id] = once variant · [name=selling_plan] = "" (removed)
 *
 * All ids/prices are server-rendered as data-* on .pdp-hero--door (single source
 * of truth). This file only reflects state into the form + UI. It ALSO mirrors
 * the mode onto <body data-door-mode> so cross-section surfaces (the founding-gift
 * callout, mode-scoped trust lines) hide/show without JS reaching into them.
 *
 * ?purchase=once is honoured on load and reflected in the URL as an
 * implementation / QA convenience ONLY — no hub customer-facing link uses it
 * (§2A.1). Idempotent; safe to re-run.
 *
 * Dawn rem base: 1rem = 10px.
 */
(function () {
  'use strict';

  function money(el, key) {
    return el ? el.getAttribute(key) || '' : '';
  }

  /*
   * Mode-swapped gallery (F20/F22): both modes carry a full 5-image set —
   * subscribe = the sub parent's media (pictures the Incubator gift), once =
   * the one-time parent's media (box contents only, no Incubator). The sets
   * are server-rendered as JSON ([data-door-gallery-sets]); swapping each
   * img's src in place keeps the thumb strip, hero-swap-on-click, and the
   * lightbox (which reads thumb srcs at open time) working unmodified.
   */
  function readGallerySets(hero) {
    if (hero._doorGallerySets !== undefined) return hero._doorGallerySets;
    var el = hero.querySelector('[data-door-gallery-sets]');
    var sets = null;
    try { sets = el ? JSON.parse(el.textContent) : null; } catch (_) { sets = null; }
    hero._doorGallerySets = sets;
    return sets;
  }

  /* GA4/GTM instrumentation (CRO round): additive dataLayer pushes — GTM decides
     what to forward. Never throws; no-ops when GTM is absent. */
  function track(evt, hero, mode, extra) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: evt, door: hero.dataset.productHandle || '', door_mode: mode };
      if (extra) for (var k in extra) payload[k] = extra[k];
      window.dataLayer.push(payload);
    } catch (_) { /* no-op */ }
  }

  function applyGallerySet(hero, list) {
    if (!list || !list.length) return;
    var gallery = hero.querySelector('.pdp-gallery');
    if (!gallery) return;

    var heroCell = gallery.querySelector('#pdp-gallery-hero');
    var heroImg = heroCell && heroCell.querySelector('img');
    if (heroImg) {
      heroImg.src = list[0].full;
      heroImg.removeAttribute('srcset');
      heroImg.alt = list[0].alt || '';
    }
    if (heroCell) heroCell.dataset.index = '0';

    gallery.querySelectorAll('.pdp-gallery__thumb').forEach(function (cell, i) {
      var entry = list[i];
      cell.style.display = entry ? '' : 'none';
      cell.classList.toggle('is-active', i === 0);
      var img = cell.querySelector('img');
      if (img && entry) {
        img.src = entry.thumb;
        img.removeAttribute('srcset');
        img.alt = entry.alt || '';
      }
    });
  }

  function initDoor(hero) {
    if (!hero || hero.dataset.doorBound === 'true') return;
    hero.dataset.doorBound = 'true';

    var cfg = hero.dataset; // subVariantId, subPlanId, subPrice, onceVariantId, oncePrice, cadenceDays
    var form = hero.querySelector('#pdp-product-form');
    var idInput = hero.querySelector('[data-door-id]');
    var planInput = hero.querySelector('[data-door-selling-plan]');
    var atc = hero.querySelector('[data-door-atc]');
    var atcLabel = hero.querySelector('[data-door-atc-label]');
    var priceEl = hero.querySelector('[data-door-price]');
    var cadenceEl = hero.querySelector('[data-door-cadence]');
    var shipsEl = hero.querySelector('[data-door-ships]');
    var perdayEl = hero.querySelector('[data-door-perday]');
    var reassureEl = hero.querySelector('[data-door-reassure]');
    var modeLabelEl = hero.querySelector('[data-door-mode-label]');
    var eyebrowEl = hero.querySelector('[data-door-eyebrow]');
    var toggleBtn = hero.querySelector('[data-door-toggle-btn]');
    var toggleLabel = hero.querySelector('[data-door-toggle-label]');

    var cadenceText = cadenceEl ? cadenceEl.textContent.trim() : '';

    function isSoldOut() {
      return !!(atc && atc.hasAttribute('disabled'));
    }

    function apply(mode) {
      var sub = mode !== 'once';
      hero.dataset.doorMode = sub ? 'subscribe' : 'once';
      // Mirror onto <body> so the founding-gift callout + mode-scoped copy respond.
      document.body.dataset.doorMode = hero.dataset.doorMode;

      // ── Gallery — swap image sets only when the mode actually changes, so the
      // boot pass never strips the server-rendered srcset in the default mode.
      var galleryMode = sub ? 'subscribe' : 'once';
      if (hero._doorGalleryMode === undefined) hero._doorGalleryMode = 'subscribe';
      if (galleryMode !== hero._doorGalleryMode) {
        var sets = readGallerySets(hero);
        if (sets) applyGallerySet(hero, sets[galleryMode]);
        hero._doorGalleryMode = galleryMode;
      }

      // ── Form contract — the whole point ──
      if (idInput) idInput.value = sub ? cfg.subVariantId : cfg.onceVariantId;
      if (planInput) planInput.value = sub ? (cfg.subPlanId || '') : '';

      // ── Price block ──
      if (priceEl) priceEl.textContent = sub ? money(priceEl, 'data-price-sub') : money(priceEl, 'data-price-once');
      if (cadenceEl) cadenceEl.style.display = sub ? '' : 'none';
      if (shipsEl) shipsEl.style.display = sub ? '' : 'none';
      if (perdayEl) perdayEl.textContent = sub ? money(perdayEl, 'data-perday-sub') : money(perdayEl, 'data-perday-once');
      if (reassureEl) reassureEl.textContent = sub ? money(reassureEl, 'data-reassure-sub') : money(reassureEl, 'data-reassure-once');
      if (modeLabelEl) modeLabelEl.textContent = sub ? money(modeLabelEl, 'data-label-sub') : money(modeLabelEl, 'data-label-once');
      if (eyebrowEl) eyebrowEl.textContent = sub ? money(eyebrowEl, 'data-eyebrow-sub') : money(eyebrowEl, 'data-eyebrow-once');

      // ── ATC label ──
      if (atcLabel) {
        if (isSoldOut()) atcLabel.textContent = 'Sold Out';
        else if (sub) atcLabel.textContent = 'Subscribe — ' + cfg.subPrice + cadenceText;
        else atcLabel.textContent = 'Buy now — ' + cfg.oncePrice;
      }

      // ── Toggle affordance ──
      if (toggleBtn) toggleBtn.setAttribute('aria-pressed', sub ? 'false' : 'true');
      if (toggleLabel) {
        toggleLabel.innerHTML = sub
          ? toggleLabel.getAttribute('data-label-to-once')
          : toggleLabel.getAttribute('data-label-to-sub');
      }

      // ── Sticky bar (door fork) — pdp-v2.js owns visibility; we own mode content.
      var stickyPrice = document.querySelector('[data-door-sticky-price]');
      var stickyMode = document.querySelector('[data-door-sticky-mode]');
      var stickyAtc = document.querySelector('.pdp-sticky-bar [data-sticky-atc]');
      if (stickyPrice) {
        stickyPrice.textContent = sub ? cfg.subPrice + cadenceText : cfg.oncePrice;
        stickyPrice.classList.toggle('pdp-sticky-bar__price--gold', sub);
      }
      if (stickyMode) stickyMode.textContent = sub ? 'Monthly subscription' : 'One-time purchase';
      if (stickyAtc) {
        if (isSoldOut()) stickyAtc.textContent = 'Sold Out';
        else stickyAtc.textContent = sub ? 'Subscribe — ' + cfg.subPrice + cadenceText : 'Buy now — ' + cfg.oncePrice;
      }

      // ── URL reflect (QA/impl hook only — never used by hub links) ──
      try {
        var url = new URL(window.location.href);
        if (sub) url.searchParams.delete('purchase');
        else url.searchParams.set('purchase', 'once');
        window.history.replaceState({ doorMode: hero.dataset.doorMode }, '', url.toString());
      } catch (_) { /* no-op */ }
    }

    if (toggleBtn) {
      toggleBtn.addEventListener('click', function (e) {
        e.preventDefault();
        var next = hero.dataset.doorMode === 'once' ? 'subscribe' : 'once';
        apply(next);
        track('door_mode_toggle', hero, next);
      });
    }

    // Submit-time guard — re-assert the id + selling_plan pair for the current
    // mode at the moment of add-to-cart, capture phase, so nothing (e.g. a hidden
    // Recharge widget) can desync the form before product-form.js serialises it.
    if (form && form.dataset.doorGuardBound !== 'true') {
      form.dataset.doorGuardBound = 'true';
      form.addEventListener('submit', function () {
        var sub = hero.dataset.doorMode !== 'once';
        if (idInput) idInput.value = sub ? cfg.subVariantId : cfg.onceVariantId;
        if (planInput) planInput.value = sub ? (cfg.subPlanId || '') : '';
        track('door_add_to_cart', hero, hero.dataset.doorMode, {
          door_price: sub ? cfg.subPrice : cfg.oncePrice,
        });
      }, true);
    }

    // Boot: honour ?purchase=once (QA convenience) else the server default mode.
    var startMode = hero.dataset.doorMode || 'subscribe';
    try {
      var params = new URLSearchParams(window.location.search);
      if (params.get('purchase') === 'once' && hero.dataset.doorOnceAvailable === 'true') startMode = 'once';
    } catch (_) { /* no-op */ }
    apply(startMode);
  }

  function init() {
    document.querySelectorAll('.pdp-hero--door').forEach(initDoor);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
